import React, { useCallback, useContext, useMemo } from 'react';
import { Button, Select, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import Table from '@components/Table';
import { PaginatedData } from '@utils/interfaces';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import {
  dateFormat,
  getDateFromBRPattern,
  getDateToBRPattern,
  getDateWithISOFormat,
} from '@utils/formatters';
import { Link } from 'react-router-dom';
import {
  CloseCircleOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import DatePicker from '@components/DatePicker';
import moment from 'moment';
import useSearchParams from '@hooks/useSearchParams';
import SessionContext from '@providers/Session/SessionContext';
import {
  ApplicationsConstants,
  motProducts,
} from '@utils/applicationsConstants';
import rolesConstants from '@utils/rolesConstants';

export interface IReport {
  id: number;
  customerId: number;
  cpfCnpj: string;
  flagExecuted: boolean;
  initialDate: string;
  finalDate: string;
  type: string;
  url: string;
  customerName: string;
}

const Reports: React.FC = () => {
  const { t } = useTranslation('reports');
  const { keycloak } = useContext(KeycloakContext);
  const {
    isRoot,
    hasPermission,
    isNotChildOfRoot,
    customer: { customerLogged },
  } = useContext(SessionContext);
  const customerType = keycloak?.idTokenParsed?.customerType;
  const isBroker = ['SUPERBROKER', 'BROKER'].includes(customerType ?? '');

  const hasNewLora = hasPermission(rolesConstants.LORAWAN_NEW);

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: ['type', 'initialDate', 'status', 'flagExecuted'],
  });

  const { type, initialDate, status, page, linesPerPage } = useMemo(
    () => searchParams,
    [searchParams],
  );

  const filters = useMemo(
    () => ({
      status,
      type: type?.toUpperCase(),
      initialDate: initialDate ? getDateToBRPattern(initialDate) : undefined,
    }),
    [status, initialDate, type],
  );

  const { data } = useSwr<PaginatedData<IReport>>(
    `/report-request/customers/${keycloak.idTokenParsed?.customerId}`,
    {
      params: {
        ...filters,
        page: page ? Number(page) - 1 : 0,
        linesPerPage: linesPerPage ?? 20,
      },
    },
    { refreshInterval: 10 * 1000 },
  );

  const selectOptions = useMemo(() => {
    const types = [
      {
        label: t('types.MULTICONNECT_INVENTORY'),
        value: 'MULTICONNECT_INVENTORY',
        products: [ApplicationsConstants.MULTI_CONNECT],
      },
      {
        label: t('types.MOT_INVENTORY'),
        value: 'MOT_INVENTORY',
        products: motProducts,
      },
      {
        label: t('types.CONNECTONE_INVENTORY'),
        value: 'CONNECTONE_INVENTORY',
        products: [ApplicationsConstants.CONNECT_ONE],
      },
      {
        label: t('types.INVOICES'),
        value: 'FATURAS',
      },
      {
        label: t('types.INVOICE_DETAIL'),
        value: 'CONSOLIDADO',
      },
      {
        label: t('types.CONSUMPTION_BILLING'),
        value: 'RESUMO DA FATURA',
      },
      {
        label: t('types.INVOICE_CONSUMPTION_DETAIL'),
        value: 'CONSUMO DIARIO',
      },
      {
        label: t('types.INVOICE_DETAIL_OPEN'),
        value: 'CONSOLIDADO ABERTA',
      },
      {
        label: t('types.CONSUMPTION_BILLING_OPEN'),
        value: 'RESUMO DA FATURA ABERTA',
      },
      {
        label: t('types.INVOICE_CONSUMPTION_DETAIL_OPEN'),
        value: 'CONSUMO DIARIO ABERTA',
      },
      {
        label: t('types.FATURAMENTO DETALHADO'),
        value: 'FATURAMENTO DETALHADO',
      },
      {
        label: t('types.RADACCT_SESSIONS_DAYS'),
        value: 'RADACCT_SESSIONS_DAYS',
      },
      {
        label: t('types.RADACCT_SESSIONS_MONTH'),
        value: 'RADACCT_SESSIONS_MONTH',
      },
      {
        label: t('types.RADACCT_SESSIONS_CYCLE'),
        value: 'RADACCT_SESSIONS_CYCLE',
      },
      {
        label: t('types.ALERT_EVENTS'),
        value: 'ALERT_EVENTS',
      },
      {
        label: t('types.TPDU_DEVICE'),
        value: 'TPDU_DEVICE',
        products: [ApplicationsConstants.MOT_PAY],
      },
      {
        label: t('types.TPDU_TRANSACTION'),
        value: 'TPDU_TRANSACTION',
        products: [ApplicationsConstants.MOT_PAY],
      },
      {
        label: t('types.LORAWAN_DEVICE'),
        value: `LORAWAN_DEVICE${hasNewLora ? '_V2' : ''}`,
        products: [ApplicationsConstants.LORAWAN],
      },
      {
        label: t('types.LORAWAN_DEVICE_MOT'),
        value: `LORAWAN_DEVICE_MOT${hasNewLora ? '_V2' : ''}`,
        products: [
          ApplicationsConstants.MOT_ENTERPRISE,
          ApplicationsConstants.MOT_BUSINESS,
        ],
      },
      {
        label: t('types.LORAWAN_MESSAGE'),
        value: `LORAWAN_MESSAGE${hasNewLora ? '_V2' : ''}`,
        products: [
          ApplicationsConstants.LORAWAN,
          ApplicationsConstants.MOT_ENTERPRISE,
          ApplicationsConstants.MOT_BUSINESS,
        ],
      },
      {
        label: t('types.SMS'),
        value: 'SMS',
      },
      {
        label: t('types.SMS_REQUESTS'),
        value: 'SMS_REQUESTS',
      },
      {
        label: t('types.SMS_HIRING'),
        value: 'SMS_HIRING',
      },
      {
        label: t('types.OPERATION_ACTION'),
        value: 'OPERATION_ACTION',
      },
      {
        label: t('types.ACTION_QUEUE'),
        value: 'ACTION_QUEUE',
      },
      {
        label: t('types.CONSUMPTION_ANALYSIS'),
        value: 'CONSUMPTION_ANALYSIS',
      },
    ];

    if (isRoot || isBroker) {
      if (!isNotChildOfRoot) {
        types.push({
          label: t('types.CANCELED_INVENTORY'),
          value: 'CANCELED_INVENTORY',
        });
      }
      types.push(
        {
          label: t('types.LORAWAN_DEVICE_NS'),
          value: 'LORAWAN_DEVICE_NS',
          products: [ApplicationsConstants.LORAWAN],
        },
        {
          label: t('types.CANCELLATION_MANAGEMENT'),
          value: 'CANCELLATION_MANAGEMENT',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.CANCELLATION_MANAGEMENT_DETAILED'),
          value: 'CANCELLATION_MANAGEMENT_DETAILED',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.CANCELLATION'),
          value: 'CANCELLATION',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.SUBSTITUTION'),
          value: 'SUBSTITUTION',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.SUSPENSION'),
          value: 'SUSPENSION',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.SUSPENSION_REVERSE'),
          value: 'SUSPENSION_REVERSE',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.FINANCIAL'),
          value: 'FINANCIAL',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.BROKER_INVOICE'),
          value: 'BROKER_INVOICE',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.CUSTOMERS'),
          value: 'CUSTOMERS',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.MANAGEMENT'),
          value: 'MANAGEMENT',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
        {
          label: t('types.BAIT_RENEWAL'),
          value: 'BAIT_RENEWAL',
          products: [ApplicationsConstants.MOT_ENTERPRISE],
        },
      );
    }

    const typesFilteredByProduct = types?.filter(item => {
      if (isRoot || !item?.products) {
        return true;
      }
      const customerProducts = customerLogged?.listContractTypes;
      return customerProducts?.some((el: any) =>
        item?.products?.includes(el.toLowerCase()),
      );
    });

    typesFilteredByProduct.sort((a, b) => a.label.localeCompare(b.label));

    return {
      types: typesFilteredByProduct,
      status: [
        {
          label: t('filters.processing'),
          value: 'F',
        },
        {
          label: t('filters.concluded'),
          value: 'T',
        },
      ],
    };
  }, [
    t,
    hasNewLora,
    isRoot,
    isBroker,
    isNotChildOfRoot,
    customerLogged?.listContractTypes,
  ]);

  const statusOptions = useMemo(
    () =>
      [
        'IN_QUEUE',
        'PROCESSING',
        'FORMATTING',
        'FINISH',
        'UNEXPECTED_ERROR',
        'WAITING_FORMATTING',
      ].map(value => ({
        label: t(`status.${value}`),
        value,
      })),
    [t],
  );

  const handleFileColumn = useCallback(
    (url: string, record: any) => {
      const reportStatus = record?.status;

      switch (reportStatus) {
        case 'UNEXPECTED_ERROR':
          return <CloseCircleOutlined style={{ color: 'red' }} />;
        case 'FINISH':
        case 'WAITING_FORMATTING':
          return (
            <Link to={`/reports/${url}`} target="_blank" download>
              <Tooltip
                title={t(
                  `download${
                    reportStatus === 'WAITING_FORMATTING'
                      ? '-unformatted-file'
                      : ''
                  }`,
                )}
              >
                <Button
                  icon={
                    <DownloadOutlined
                      style={{
                        fontSize: '16px',
                        color: 'var(--primary-color)',
                      }}
                    />
                  }
                />
              </Tooltip>
            </Link>
          );
        default:
          return (
            <LoadingOutlined
              style={{
                fontSize: '16px',
                color: 'var(--primary-color)',
              }}
            />
          );
      }
    },
    [t],
  );

  const columns = useMemo(
    () => [
      { title: t('table.user'), dataIndex: 'customerName' },
      {
        title: t('table.type'),
        dataIndex: 'type',
        render: (value: string) => t(`types.${value}`),
      },
      {
        title: t('table.start-request'),
        dataIndex: 'initialDate',
        render: dateFormat,
        align: 'center' as const,
      },
      {
        title: t('table.final-request'),
        dataIndex: 'finalDate',
        render: dateFormat,
        align: 'center' as const,
      },
      {
        title: t('table.status'),
        dataIndex: 'status',
        align: 'center' as const,
        render: (value: string) => value && t(`status.${value}`),
      },
      {
        title: t('table.file'),
        dataIndex: 'url',
        align: 'center' as const,
        render: handleFileColumn,
      },
    ],
    [handleFileColumn, t],
  );

  const onChangeInitialDate = useCallback(
    (_: any, dateString: any) => {
      const formattedDate = getDateFromBRPattern(dateString);
      setSearchParams({ initialDate: formattedDate });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('table.type'),
        element: (
          <Select
            optionFilterProp="label"
            options={selectOptions?.types}
            style={{ width: '100%' }}
            value={type?.toUpperCase()}
            allowClear
            showSearch
            onChange={value => {
              setSearchParams({
                type: value?.toLowerCase(),
              });
            }}
          />
        ),
      },
      {
        visible: true,
        label: t('table.from-request'),
        element: (
          <DatePicker
            allowClear
            format="DD/MM/YYYY"
            disabledDate={d => d && d > moment().endOf('day')}
            size="middle"
            value={initialDate && moment(getDateWithISOFormat(initialDate))}
            style={{ width: '100%' }}
            onChange={onChangeInitialDate}
          />
        ),
      },
      {
        visible: true,
        label: t('table.status'),
        element: (
          <Select
            optionFilterProp="label"
            options={statusOptions}
            style={{ width: '100%' }}
            value={status}
            allowClear
            showSearch
            onChange={value => setSearchParams({ status: value })}
          />
        ),
      },
    ],
    [
      t,
      selectOptions?.types,
      type,
      initialDate,
      onChangeInitialDate,
      statusOptions,
      status,
      setSearchParams,
    ],
  );

  return (
    <div>
      <Table
        columns={columns}
        data={data?.content || []}
        loading={!data}
        title={t('title')}
        tableKey="reports"
        filters={{
          list: tableFilters,
          openByDefault: true,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
        onTableChange={setSearchParams}
      />
    </div>
  );
};

export default Reports;
